// Thin wrapper around Google Places API v1 (Autocomplete + Details).
// Keep this file dependency-free so it can be reused anywhere we need
// address suggestions or place lookups.

const GOOGLE_MAPS_API_KEY =
  (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
const PLACES_AUTOCOMPLETE_URL =
  "https://places.googleapis.com/v1/places:autocomplete";
const PLACES_DETAILS_BASE_URL = "https://places.googleapis.com/v1/places/";

export type PlacesSuggestion = {
  placeId: string;
  mainText: string;
  secondaryText?: string;
  description: string;
};

export type PlaceDetails = {
  address: string;
  lat: number;
  lng: number;
};

function requireGoogleApiKey() {
  if (!GOOGLE_MAPS_API_KEY) {
    throw new Error("Google Maps API key not configured");
  }
  return GOOGLE_MAPS_API_KEY;
}

export function createPlacesSessionToken(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export async function fetchPlacesAutocomplete(
  input: string,
  sessionToken?: string | null,
  opts?: { signal?: AbortSignal }
): Promise<PlacesSuggestion[]> {
  const apiKey = requireGoogleApiKey();
  const res = await fetch(PLACES_AUTOCOMPLETE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": apiKey,
      "X-Goog-FieldMask":
        "suggestions.placePrediction.placeId," +
        "suggestions.placePrediction.place," +
        "suggestions.placePrediction.text.text," +
        "suggestions.placePrediction.structuredFormat.mainText.text," +
        "suggestions.placePrediction.structuredFormat.secondaryText.text",
    },
    body: JSON.stringify({
      input,
      sessionToken: sessionToken || undefined,
    }),
    signal: opts?.signal,
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(
      `Places autocomplete failed (${res.status})${msg ? `: ${msg}` : ""}`
    );
  }

  const data = await res.json();
  const suggestions = Array.isArray(data?.suggestions) ? data.suggestions : [];
  return suggestions
    .map((s: any) => s?.placePrediction)
    .filter(Boolean)
    .map((p: any) => {
      const placeId =
        p?.placeId ||
        (typeof p?.place === "string" ? p.place.split("/").pop() : "");
      const mainText =
        p?.structuredFormat?.mainText?.text || p?.text?.text || "";
      const secondaryText = p?.structuredFormat?.secondaryText?.text || "";
      const description =
        p?.text?.text ||
        `${mainText}${secondaryText ? `, ${secondaryText}` : ""}` ||
        "";
      return {
        placeId,
        mainText,
        secondaryText,
        description,
      } as PlacesSuggestion;
    })
    .filter((p: PlacesSuggestion) => !!p.placeId);
}

export async function fetchPlaceDetails(
  placeId: string,
  sessionToken?: string | null
): Promise<PlaceDetails> {
  const apiKey = requireGoogleApiKey();
  const url = new URL(
    `${PLACES_DETAILS_BASE_URL}${encodeURIComponent(placeId)}`
  );
  if (sessionToken) url.searchParams.set("sessionToken", sessionToken);

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": apiKey,
      "X-Goog-FieldMask": "formattedAddress,location",
    },
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(
      `Place details failed (${res.status})${msg ? `: ${msg}` : ""}`
    );
  }

  const data = await res.json();
  const location = data?.location;
  const lat = location?.latitude;
  const lng = location?.longitude;
  if (typeof lat !== "number" || typeof lng !== "number") {
    throw new Error("Place details missing coordinates");
  }
  return {
    address: data?.formattedAddress || placeId,
    lat,
    lng,
  };
}
